import { cycleFrames } from './coin';
import Phaser from 'phaser';
import type { EnemyState } from '../core/enemy';
import type { EnemyType } from '../core/types';

/** Width of the HP bar drawn above each enemy sprite. */
const HP_BAR_W = 32;
/** Height of the HP bar. */
const HP_BAR_H = 4;
/** Pinned gameplay display size for regular enemies (matches placeholder). */
const ENEMY_DISPLAY_SIZE = 32;
/**
 * Per-type display-size overrides — heavies render larger for visual weight:
 * bosses biggest, then the tanky Ultra Nugget, then the spider-riding Jockey.
 */
const DISPLAY_SIZE_OVERRIDES: Partial<Record<EnemyType, number>> = {
  boss: 52,
  nullnugget: 52,
  ultranugget: 46,
  jockey: 42,
  watcher: 52,
  nuggetguard: 38, // helmeted swordsman — a touch bigger than a trooper
  kingnuggetron: 64, // the huge crowned king — largest sprite in the game
  carrotcrusher: 38, // sword-wielding carrot — reads a touch bigger than a trooper
  carrotninja: 40, // twin-sword carrot — its taller stem earns a couple of extra pixels
  brocco: 38, // red-eyed broccoli
  potatoslicer: 44, // the tanky knife-and-hammer potato — largest of the castle roster
  drycarrot: 40, // the dust-caked desert husk — Ninja-sized
  minibell: 24, // the emperor's tiny pepper minion — smallest sprite in the game
  emperorpepro: 64, // the emperor on his sandy cloud — King Nugget-tron sized
  caulifire: 46, // the charred cauliflower wall — Ultra-Nugget sized bulk
  esparagusmage: 42, // the hatted asparagus wizard — tall stalk plus pointed hat
  ocraa: 40, // the horned-helmet okra viking — Ninja-sized with the helm on top
  bamboocrew: 48, // three stalks abreast — the widest non-boss silhouette
  peproherald: 64, // the guttered-out emperor — drawn at the emperor's own size
  kingember: 72, // the blue flame king on his fire cloud — the final boss, the biggest of all
  microwavednugget: 34, // a trooper, hot from the oven
  combo199: 46, // two nuggets and their box
  chiply: 42, // an angry crisp, taller than a trooper
  microwave: 60, // a whole legless oven, hopping — bigger than any trooper
  chiplysdad: 88, // the crowned crisp — The Moon's boss, twice Chiply and past King Ember
  nacho: 40, // a tortilla chip, Chiply-sized
  seasonezchippington: 42, // Chiply in a bucket hat
  lifeguard: 40, // a Corroder in a cap
  princenacho: 76, // the crowned, caped nacho — a boss, between Chiply's Dad and King Ember
  princenacho2: 170, // the SECOND form: the biggest thing in the game, by design
  darknacho: 48, // "a bit bigger than a normal nacho" (40)
  jackoghost: 38, // the Ghost plus a pumpkin on his head
  blueghost: 34,
  reaper: 170, // The Shadow Reaper floats where Prince Nacho's second form does
  lordturtleshell: 200, // "a HUGE turtle" — Heaven's Peak's boss, over the sea
  winger: 42, // the Carrot Crusher plus a wingspan
  twilight: 34,
  nightfly: 34,
  // Halolight and his splits: "smaller", then "even smaller".
  halolight: 42,
  halolight2: 30,
  halolight3: 22,
  // The Mushy: "a redshroom but bigger" — a boss, Chiply's Dad sized.
  themushy: 88,
  kingshroometron: 104,
};

/** On-screen size (px) an enemy type is drawn at. */
export function enemyDisplaySize(type: EnemyType): number {
  return DISPLAY_SIZE_OVERRIDES[type] ?? ENEMY_DISPLAY_SIZE;
}

/**
 * Render-only levitation: these enemies draw this many px ABOVE their sim
 * position (which stays on the track), with a soft shadow left on the ground
 * and a gentle bob. The sim, targeting, and hover tooltip all keep using the
 * on-track position.
 */
const HOVER_OFFSETS: Partial<Record<EnemyType, number>> = {
  watcher: 14,
  emperorpepro: 14, // floats on his sandy cloud
  peproherald: 14, // the same emperor, the same cloud (thinner, but still a cloud)
  kingember: 16, // rides a cloud of fire, clear of the ground
  chiplysdad: 16, // floats on his salt-fall
  princenacho: 16, // "a nacho that is floating"
  princenacho2: 0, // the second form is anchored in mid-air already; no extra lift
  ghost: 12, // no legs — the ghosts drift above the track
  darkghost: 12,
  jackoghost: 12,
  blueghost: 12,
  reaper: 0, // anchored in mid-air already, like Prince Nacho's second form
  // Above the Clouds: everything with wings flies over the track.
  winger: 12,
  twilight: 12,
  nightfly: 12,
  halolight: 12,
  halolight2: 10,
  halolight3: 8,
};
/**
 * Sprite opacity per type (default 1). The Ghost is "mostly transparent", and
 * the generator cannot ship partial alpha, so the see-through look is drawn
 * here; the Dark Ghost is "the exact same thing, but red". Render-only.
 */
export const ENEMY_ALPHA: Partial<Record<EnemyType, number>> = {
  ghost: 0.55,
  darkghost: 0.6,
  // Jack'O and the Blue Ghost are the same ghost recoloured: see-through too,
  // a touch more solid so the pumpkin and the pale blue still read.
  jackoghost: 0.7,
  blueghost: 0.65,
};
/**
 * A caption drawn above the HP bar for the whole of an enemy's walk — The
 * Lifeguard's "LIFEGUARD!" (spec 2026-09-09 §3). Render-only.
 */
const CAPTIONS: Partial<Record<EnemyType, string>> = {
  lifeguard: 'LIFEGUARD!',
  princenacho: 'PRINCE NACHO',
};

/**
 * Bosses that ARRIVE from above (The Shadow Reaper, spec 2026-10-07: "comes
 * down from the top of the screen"): the sprite starts this far above its
 * anchored spot and sinks into place over DESCEND_MS. Render-only — the sim
 * has him there, and hittable, from the first tick.
 */
const DESCENDERS: Partial<Record<EnemyType, number>> = {
  reaper: 420,
  // Lord Turtleshell floats UP out of the sea (spec 2026-10-12): a negative
  // drop starts him below his spot, and he fades in as he rises.
  lordturtleshell: -170,
};
/**
 * Flying animation: bosses whose wings flap by alternating two frames
 * (Lord Turtleshell's raised and lowered wings), and how long each shows.
 */
const WING_FRAMES: Partial<Record<EnemyType, { frames: readonly string[]; ms: number }>> = {
  lordturtleshell: {
    frames: ['enemy-lordturtleshell', 'enemy-lordturtleshell-2'],
    ms: 240,
  },
};
const DESCEND_MS = 1600;
/**
 * A slow, deep float for an anchored boss that hangs in the air (the
 * Reaper's "floating animation"): amplitude px and period ms.
 */
const FLOATERS: Partial<Record<EnemyType, { px: number; ms: number }>> = {
  reaper: { px: 9, ms: 2600 },
  lordturtleshell: { px: 8, ms: 2200 },
};
/** Bosses whose `swing` is a SCYTHE — a big wind-up and follow-through. */
const SCYTHE_SWINGERS = new Set<EnemyType>(['reaper']);

/** Amplitude (px) and period (ms) of the levitation bob. */
const HOVER_BOB_PX = 3;
const HOVER_BOB_MS = 900;

/**
 * Render-only hop: these enemies bounce along the trail instead of gliding
 * (the Microwave, the Fungi Forest's shrooms). Height (px) and period (ms) of
 * one hop; the cord's length (CORD_TYPES). The sim position stays on the track.
 */
const HOP_TYPES: Partial<Record<EnemyType, number>> = {
  microwave: 12,
  // The Fungi Forest's shrooms hop the Microwave's hop, with no cord.
  redshroom: 12,
  poisonshroom: 12,
  skullshroom: 12,
  purplefungi: 12,
  // The Mushy's bigger body takes a bigger, heavier hop.
  themushy: 18,
  glowshroom: 12,
  blizzardfungi: 12,
  zombieshroom: 12,
  hynofungi: 12,
  // The crowned King's hop is The Mushy's heavy one, and heavier.
  kingshroometron: 20,
};
/** Hoppers that drag the power cord — the Microwave alone. */
const CORD_TYPES = new Set<EnemyType>(['microwave']);
const HOP_MS = 520;
const CORD_LEN = 40;
/** Enemies that drip salt (Chiply's Dad): a few white grains fall from the sprite. */
const SALT_TYPES = new Set<EnemyType>(['chiplysdad']);
const SALT_MS = 700;

/**
 * Enemy types that render with ANOTHER type's art — for a variant that is
 * genuinely the same character and does not warrant its own sprite.
 *
 * The Pepro Herald used to alias `emperorpepro` and no longer does: it ships
 * `enemy-peproherald`, the emperor chained from his own PNG with the flame mane
 * guttered down to embers, so the weaker version LOOKS weaker on the track.
 * Empty is a valid state — keep the indirection, it is what lets a variant land
 * without touching every art call site.
 */
const TEXTURE_ALIASES: Partial<Record<EnemyType, EnemyType>> = {
  // Halolight's splits are the same Halolight, drawn smaller (ENEMY_SIZES).
  halolight2: 'halolight',
  halolight3: 'halolight',
};

/** Atlas texture key for an enemy type, following any alias. */
export function textureKeyFor(type: EnemyType): string {
  return `enemy-${TEXTURE_ALIASES[type] ?? type}`;
}

/** Blue tint applied when the enemy has a 'slow' status. */
const TINT_SLOW = 0x4a90d9;
/** Purple tint applied when the enemy has a 'vulnerable' status. */
const TINT_VULNERABLE = 0x9b59b6;
/** Toxic green tint while Bow Hambi's poison is ticking. */
const TINT_POISON = 0x7ccd3a;
/** Pale icy tint applied to every enemy while a map-wide freeze is active. */
const TINT_FROZEN = 0xbfe3f2;
/** Snowshroom orange-white while an enemy is SHROOMNITIZED (spec 2026-10-24). */
const TINT_SHROOMNITIZED = 0xffb070;

/**
 * EnemyView — thin wrapper binding a Phaser sprite and HP bar
 * to a single EnemyState by id.
 *
 * Depth layers:
 *   sprite  → 4
 *   hpBg    → 5
 *   hpBar   → 6
 */
export class EnemyView {
  readonly id: number;

  private readonly sprite: Phaser.GameObjects.Sprite;
  private readonly hpBg: Phaser.GameObjects.Rectangle;
  private readonly hpBar: Phaser.GameObjects.Rectangle;
  /** Ground shadow under levitating enemies (only created for HOVER types). */
  private readonly shadow?: Phaser.GameObjects.Ellipse;
  /** Cached HP-bar Y offset (4px above sprite top) for this enemy type. */
  private readonly hpBarOffsetY: number;
  /** Base levitation height (0 for grounded enemies). */
  private readonly hoverOffset: number;
  /** Hop height (0 for enemies that glide). */
  private readonly hopHeight: number;
  /** The power cord dragged by a hopping enemy (Microwave only). */
  private readonly cord?: Phaser.GameObjects.Graphics;
  /** Direction the cord trails in — kept from the last frame the enemy moved. */
  private cordDir = { x: -1, y: 0 };
  /** Falling salt grains (Chiply's Dad only). */
  private readonly salt?: Phaser.GameObjects.Graphics;
  /** The caption over the HP bar (CAPTIONS types only). */
  private readonly caption?: Phaser.GameObjects.Text;
  /** The staff swing, while one is playing (see `swing`). */
  private swingTween?: Phaser.Tweens.BaseTween;
  /** Px still to sink before a DESCENDER reaches its spot (tweened to 0). */
  private readonly descent = { y: 0 };
  private readonly float?: { px: number; ms: number };
  private readonly scythe: boolean;

  constructor(scene: Phaser.Scene, state: EnemyState) {
    this.id = state.id;

    const displaySize = DISPLAY_SIZE_OVERRIDES[state.type] ?? ENEMY_DISPLAY_SIZE;
    this.hoverOffset = HOVER_OFFSETS[state.type] ?? 0;
    this.float = FLOATERS[state.type];
    this.scythe = SCYTHE_SWINGERS.has(state.type);
    const drop = DESCENDERS[state.type];
    if (drop) {
      this.descent.y = drop;
      scene.tweens.add({
        targets: this.descent,
        y: 0,
        duration: DESCEND_MS,
        ease: 'Cubic.easeOut',
      });
    }
    this.hopHeight = HOP_TYPES[state.type] ?? 0;
    if (CORD_TYPES.has(state.type)) this.cord = scene.add.graphics().setDepth(3.95);
    if (SALT_TYPES.has(state.type)) this.salt = scene.add.graphics().setDepth(3.95);
    const caption = CAPTIONS[state.type];
    if (caption) {
      this.caption = scene.add
        .text(state.pos.x, state.pos.y, caption, {
          fontSize: '14px',
          fontStyle: 'bold',
          color: '#ffffff',
          stroke: '#1a1a1a',
          strokeThickness: 3,
        })
        .setOrigin(0.5, 1)
        .setDepth(5)
        .setName(`caption-${state.type}`);
    }
    // HP bar sits 4px above the sprite top, whatever the sprite's size.
    this.hpBarOffsetY = -(displaySize / 2) - 4;

    if (this.hoverOffset > 0) {
      this.shadow = scene.add
        .ellipse(state.pos.x, state.pos.y + displaySize * 0.3, displaySize * 0.7, 10, 0x000000, 0.3)
        .setDepth(3.9);
    }

    // Pin display size so sprites always render at gameplay size regardless of
    // the texture's native resolution (placeholder = 32px; real art may differ).
    this.sprite = scene.add
      .sprite(state.pos.x, state.pos.y, textureKeyFor(state.type))
      .setDepth(4)
      .setDisplaySize(displaySize, displaySize)
      .setAlpha(ENEMY_ALPHA[state.type] ?? 1);
    // Rising out of the water: from faint to solid while he comes up.
    if (drop !== undefined && drop < 0) {
      this.sprite.setAlpha(0.25);
      scene.tweens.add({ targets: this.sprite, alpha: 1, duration: DESCEND_MS });
    }
    const wings = WING_FRAMES[state.type];
    if (wings && wings.frames.every((k) => scene.textures.exists(k))) {
      cycleFrames(scene, this.sprite, wings.frames, wings.ms);
    }

    // HP bar background (full width, dark grey)
    this.hpBg = scene.add
      .rectangle(
        state.pos.x - HP_BAR_W / 2,
        state.pos.y + this.hpBarOffsetY,
        HP_BAR_W,
        HP_BAR_H,
        0x333333,
      )
      .setOrigin(0, 0.5)
      .setDepth(5);

    // HP bar fill (scales to reflect remaining HP)
    this.hpBar = scene.add
      .rectangle(
        state.pos.x - HP_BAR_W / 2,
        state.pos.y + this.hpBarOffsetY,
        HP_BAR_W,
        HP_BAR_H,
        0x00dd44,
      )
      .setOrigin(0, 0.5)
      .setDepth(6);

    this.update(state);
  }

  /** Sync sprite position, HP bar width, and status tint to the latest state. */
  update(state: EnemyState, frozen = false): void {
    const { x } = state.pos;
    // Levitators float above their on-track sim position with a gentle bob
    // (frozen enemies hang still); grounded enemies draw exactly at it.
    const bob =
      this.hoverOffset > 0 && !frozen
        ? Math.sin((this.sprite.scene.time.now / HOVER_BOB_MS) * Math.PI * 2) * HOVER_BOB_PX
        : 0;
    // Hoppers bounce: |sin| keeps them on or above the track, never below.
    const hop =
      this.hopHeight > 0 && !frozen
        ? Math.abs(Math.sin((this.sprite.scene.time.now / HOP_MS) * Math.PI)) * this.hopHeight
        : 0;
    const float =
      this.float && !frozen
        ? Math.sin((this.sprite.scene.time.now / this.float.ms) * Math.PI * 2) * this.float.px
        : 0;
    const y = state.pos.y - this.hoverOffset + bob - hop + float - this.descent.y;

    this.sprite.setPosition(x, y);
    if (this.cord) this.drawCord(state, x, y);
    if (this.salt) this.drawSalt(x, y);
    this.shadow?.setPosition(x, state.pos.y + this.sprite.displayHeight * 0.3);

    // HP bar — left-anchored so it shrinks from the right as HP drops
    const ratio = Math.max(0, state.hp / state.maxHp);
    const barX = x - HP_BAR_W / 2;
    const barY = y + this.hpBarOffsetY;

    this.hpBg.setPosition(barX, barY);
    this.hpBar.setPosition(barX, barY);
    this.hpBar.setSize(HP_BAR_W * ratio, HP_BAR_H);
    this.caption?.setPosition(x, barY - 6);

    // Status tint (slow wins over poison, poison over vulnerable)
    const hasSlow = state.statuses.some((s) => s.kind === 'slow');
    const hasPoison = state.statuses.some((s) => s.kind === 'poison');
    const hasVulnerable = state.statuses.some((s) => s.kind === 'vulnerable');

    if (frozen) {
      this.sprite.setTint(TINT_FROZEN);
    } else if (state.shroomnitized) {
      // Brief (one second) and the thing to watch: it wins over the statuses.
      this.sprite.setTint(TINT_SHROOMNITIZED);
    } else if (hasSlow) {
      this.sprite.setTint(TINT_SLOW);
    } else if (hasPoison) {
      this.sprite.setTint(TINT_POISON);
    } else if (hasVulnerable) {
      this.sprite.setTint(TINT_VULNERABLE);
    } else {
      this.sprite.clearTint();
    }
  }

  /**
   * The Microwave's power cord: a sagging line trailing behind the direction
   * of travel, plug on the end, anchored to the sprite's base so it swings
   * with every hop.
   */
  private drawCord(state: EnemyState, x: number, y: number): void {
    const g = this.cord!;
    const speed = Math.hypot(state.vel.x, state.vel.y);
    if (speed > 1) this.cordDir = { x: -state.vel.x / speed, y: -state.vel.y / speed };
    const ax = x;
    const ay = y + this.sprite.displayHeight * 0.3;
    const ex = ax + this.cordDir.x * CORD_LEN;
    const ey = state.pos.y + this.sprite.displayHeight * 0.3 + this.cordDir.y * CORD_LEN;
    g.clear();
    g.lineStyle(2, 0x1a1a1a, 1);
    g.beginPath();
    g.moveTo(ax, ay);
    // A sag halfway along, so the cord reads as dragging, not as a rod.
    const mx = (ax + ex) / 2 - this.cordDir.y * 6;
    const my = (ay + ey) / 2 + 8;
    g.lineTo(mx, my);
    g.lineTo(ex, ey);
    g.strokePath();
    g.fillStyle(0x2b2b2b, 1);
    g.fillRect(ex - 4, ey - 3, 8, 6);
  }

  /** Salt grains falling from the boss's lower edge, on a rolling loop. */
  private drawSalt(x: number, y: number): void {
    const g = this.salt!;
    g.clear();
    const t = this.sprite.scene.time.now / SALT_MS;
    const base = y + this.sprite.displayHeight * 0.35;
    for (let i = 0; i < 6; i++) {
      const phase = (t + i * 0.17) % 1;
      g.fillStyle(0xffffff, 1 - phase);
      g.fillRect(x - 15 + i * 6, base + phase * 26, 3, 3);
    }
  }

  /** Remove all Phaser game objects owned by this view. */
  /**
   * Swing the staff: a quick rock out and back, played the frame this enemy
   * launches a shot (Prince Nacho's spears). One at a time — a second call
   * while the first is running is ignored rather than fighting it.
   */
  swing(): void {
    if (this.swingTween?.isPlaying()) return;
    if (this.scythe) {
      // The Reaper's scythe: wind back, then sweep through and settle.
      this.swingTween = this.sprite.scene.tweens.chain({
        targets: this.sprite,
        tweens: [
          { angle: 18, duration: 180, ease: 'Sine.easeOut' },
          { angle: -32, duration: 160, ease: 'Cubic.easeIn' },
          { angle: 0, duration: 320, ease: 'Back.easeOut' },
        ],
        onComplete: () => this.sprite.setAngle(0),
      });
      return;
    }
    this.swingTween = this.sprite.scene.tweens.add({
      targets: this.sprite,
      angle: -22,
      duration: 130,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => this.sprite.setAngle(0),
    });
  }

  destroy(): void {
    this.swingTween?.stop();
    this.sprite.destroy();
    this.hpBg.destroy();
    this.hpBar.destroy();
    this.shadow?.destroy();
    this.cord?.destroy();
    this.salt?.destroy();
    this.caption?.destroy();
  }
}
